/**
 * Single source of truth for pricing.
 *
 * Stripe Price IDs are read from env at runtime — only the metadata
 * (display name, copy, feature list) lives here.
 */

export type BillingInterval = "month" | "year";

export type TierId =
  | "trainee_monthly"
  | "trainee_yearly"
  | "pro_monthly"
  | "pro_yearly";

export interface PricingTier {
  id: TierId;
  name: string;
  group: "trainee" | "pro";
  interval: BillingInterval;
  priceCents: number;
  intervalLabel: string;
  badge?: string;
  highlight?: boolean;
  /** Env var that holds the Stripe Price ID for this tier. */
  priceEnvKey:
    | "STRIPE_PRICE_TRAINEE_MONTHLY"
    | "STRIPE_PRICE_TRAINEE_YEARLY"
    | "STRIPE_PRICE_PRO_MONTHLY"
    | "STRIPE_PRICE_PRO_YEARLY";
  features: string[];
}

export const TIERS: Record<TierId, PricingTier> = {
  trainee_monthly: {
    id: "trainee_monthly",
    name: "Trainee",
    group: "trainee",
    interval: "month",
    priceCents: 900,
    intervalLabel: "/ month",
    priceEnvKey: "STRIPE_PRICE_TRAINEE_MONTHLY",
    features: [
      "Full .65 question bank",
      "Block Prep timed exams",
      "ATSA prep (all 7 subtests + mock)",
      "FSRS-6 spaced repetition",
      "Mobile + desktop, offline-capable",
      "Cancel any time",
    ],
  },
  trainee_yearly: {
    id: "trainee_yearly",
    name: "Trainee",
    group: "trainee",
    interval: "year",
    priceCents: 8900,
    intervalLabel: "/ year",
    badge: "Save $19",
    priceEnvKey: "STRIPE_PRICE_TRAINEE_YEARLY",
    features: [
      "Everything in monthly",
      "Two months free vs. monthly billing",
      "Locks the price for the year",
    ],
  },
  pro_monthly: {
    id: "pro_monthly",
    name: "Pro",
    group: "pro",
    interval: "month",
    priceCents: 2900,
    intervalLabel: "/ month",
    priceEnvKey: "STRIPE_PRICE_PRO_MONTHLY",
    features: [
      "Everything in Trainee",
      "Phraseology Pro audio drills",
      "Endless Practice — LLM controller, no daily cap",
      "Tower / TRACON / Center career tracks",
      "Priority email support",
    ],
  },
  pro_yearly: {
    id: "pro_yearly",
    name: "Pro",
    group: "pro",
    interval: "year",
    priceCents: 24900,
    intervalLabel: "/ year",
    badge: "Save $99",
    highlight: true,
    priceEnvKey: "STRIPE_PRICE_PRO_YEARLY",
    features: [
      "Everything in Pro monthly",
      "Best value for full-time studiers",
      "Locks the price for the year",
    ],
  },
};

/**
 * All currently-buyable tiers. The Lifetime SKU was retired in May
 * 2026 and is no longer listed here — existing Lifetime holders keep
 * their entitlement through the DB's `subscription_tier` enum, which
 * still recognises `lifetime` as a Pro-equivalent grant.
 */
export function activeTiers(): PricingTier[] {
  return Object.values(TIERS);
}

/**
 * Display metadata for the Free tier. Not part of `TIERS` because
 * there is no Stripe SKU — Free is the default profile state for
 * any signed-up account. Surfaced as an explicit pricing card so
 * the funnel says "sign up costs nothing" instead of hiding the
 * onramp behind the paid plans.
 *
 * The price renders as `$0` and the CTA links to /signup on the
 * app domain. Existing accounts on `free` show "Current plan".
 */
export interface FreeTierMeta {
  id: "free";
  name: string;
  group: "free";
  priceCents: 0;
  intervalLabel: string;
  features: string[];
}

export const FREE_TIER: FreeTierMeta = {
  id: "free",
  name: "Free",
  group: "free",
  priceCents: 0,
  intervalLabel: "forever",
  features: [
    "Sample .65 question bank",
    "FSRS-6 spaced repetition",
    "ATSA mock exam (timed)",
    "5 phraseology scenarios / day",
    "Mobile + desktop, offline-capable",
  ],
};

export function formatUsd(cents: number): string {
  if (cents % 100 === 0) return `$${cents / 100}`;
  return `$${(cents / 100).toFixed(2)}`;
}

export function getTier(id: TierId): PricingTier {
  return TIERS[id];
}
